import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Plus, Pencil, Trash2, Send, Eye, CheckCircle2, Archive,
  RefreshCw, Mail, Smartphone, MessageCircle, Settings, ScrollText, ExternalLink,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { StatusBadge } from '@/components/ui/status-badge';
import { TabBar } from '@/components/ui/tab-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { FormDialog } from '@/components/ui/form-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PaginationBar } from '@/components/ui/pagination-bar';
import toast from 'react-hot-toast';

type Channel = 'EMAIL' | 'SMS' | 'WHATSAPP';
type TemplateStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
type MetaStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';

interface CommsEvent {
  eventKey: string;
  label: string;
  description: string;
  variables: string[];
  channels: Channel[];
  /** POS-I240: the e-mail of this event is a document; the Powered by line is appended after the text. */
  document?: boolean;
}

interface MessageTemplate {
  id: string;
  channel: Channel;
  eventKey: string;
  name: string;
  locale: string;
  subject?: string | null;
  body: string;
  variables: string[];
  status: TemplateStatus;
  version: number;
  metaTemplateName?: string | null;
  metaStatus: MetaStatus;
  metaRejectReason?: string | null;
  updatedAt: string;
}

interface MessageLogRecord {
  id: string;
  channel: Channel;
  eventKey?: string | null;
  toAddress: string;
  renderedSubject?: string | null;
  renderedBody: string;
  status: string;
  provider?: string | null;
  error?: string | null;
  fallbackLink?: string | null;
  createdAt: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const CHANNEL_ICON: Record<Channel, React.ComponentType<{ className?: string }>> = {
  EMAIL: Mail,
  SMS: Smartphone,
  WHATSAPP: MessageCircle,
};

const emptyForm = () => ({
  channel: 'EMAIL' as Channel,
  eventKey: '',
  name: '',
  locale: 'en',
  subject: '',
  body: '',
});

type TemplateForm = ReturnType<typeof emptyForm>;

export function CommunicationsPage() {
  const { t } = useTranslation('communications');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('communications:create') || hasPermission('communications:*');
  const canUpdate = hasPermission('communications:update') || hasPermission('communications:*');
  const canDelete = hasPermission('communications:delete') || hasPermission('communications:*');
  const canSend = hasPermission('communications:send') || hasPermission('communications:*');
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [tab, setTab] = useState<'templates' | 'logs' | 'providers'>('templates');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [form, setForm] = useState<TemplateForm>(emptyForm());
  const [previewText, setPreviewText] = useState<{ subject?: string; body: string } | null>(null);

  const [testTemplate, setTestTemplate] = useState<MessageTemplate | null>(null);
  const [testTo, setTestTo] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [logPage, setLogPage] = useState(1);
  const [logChannel, setLogChannel] = useState<Channel | ''>('');

  const { data: events } = useQuery<CommsEvent[]>({
    queryKey: ['comms-events'],
    queryFn: () => api.get('/communications/events').then((r) => r.data),
  });

  const { data: templates, isLoading } = useQuery<MessageTemplate[]>({
    queryKey: ['comms-templates'],
    queryFn: () => api.get('/communications/templates').then((r) => r.data),
  });

  const { data: logs, isLoading: logsLoading } = useQuery<Paginated<MessageLogRecord>>({
    queryKey: ['comms-logs', logPage, logChannel],
    queryFn: () =>
      api
        .get('/communications/logs', {
          params: { page: logPage, limit: 20, ...(logChannel ? { channel: logChannel } : {}) },
        })
        .then((r) => r.data),
    enabled: tab === 'logs',
  });

  const selectedEvent = useMemo(
    () => events?.find((e) => e.eventKey === form.eventKey),
    [events, form.eventKey],
  );

  const createMutation = useMutation({
    mutationFn: (data: TemplateForm) =>
      api.post('/communications/templates', {
        channel: data.channel,
        eventKey: data.eventKey,
        name: data.name,
        locale: data.locale,
        subject: data.channel === 'EMAIL' ? data.subject : undefined,
        body: data.body,
        variables: selectedEvent?.variables ?? [],
      }),
    onSuccess: () => {
      toast.success('Template created');
      qc.invalidateQueries({ queryKey: ['comms-templates'] });
      closeEditor();
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to create template'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TemplateForm> }) =>
      api.patch(`/communications/templates/${id}`, {
        name: data.name,
        subject: data.channel === 'EMAIL' ? data.subject : undefined,
        body: data.body,
      }),
    onSuccess: () => {
      toast.success('Template updated');
      qc.invalidateQueries({ queryKey: ['comms-templates'] });
      closeEditor();
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to update template'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'activate' | 'archive' }) =>
      api.patch(`/communications/templates/${id}/${action}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comms-templates'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to change status'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/communications/templates/${id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['comms-templates'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to delete template'),
  });

  const metaSyncMutation = useMutation({
    mutationFn: (id: string) => api.post(`/communications/templates/${id}/meta-sync`).then((r) => r.data),
    onSuccess: (data: { configured?: boolean; message?: string }) => {
      if (data?.configured === false) {
        toast(data.message ?? 'Meta WhatsApp not configured', { icon: '⚙️' });
      } else {
        toast.success('Submitted to Meta for review');
      }
      qc.invalidateQueries({ queryKey: ['comms-templates'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Meta sync failed'),
  });

  const testSendMutation = useMutation({
    mutationFn: ({ template, to }: { template: MessageTemplate; to: string }) =>
      api
        .post('/communications/test-send', {
          channel: template.channel,
          eventKey: template.eventKey,
          to,
        })
        .then((r) => r.data),
    onSuccess: (data: { status?: string; fallback?: boolean; fallbackLink?: string; configPath?: string }) => {
      if (data?.fallback) {
        toast(
          'No provider configured — content generated for manual send.',
          { icon: '⚠️', duration: 5000 },
        );
        if (data.fallbackLink) window.open(data.fallbackLink, '_blank');
      } else {
        toast.success('Test message sent');
      }
      setTestTemplate(null);
      setTestTo('');
      qc.invalidateQueries({ queryKey: ['comms-logs'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Test send failed'),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setPreviewText(null);
    setEditorOpen(true);
  }

  function openEdit(tpl: MessageTemplate) {
    setEditing(tpl);
    setForm({
      channel: tpl.channel,
      eventKey: tpl.eventKey,
      name: tpl.name,
      locale: tpl.locale,
      subject: tpl.subject ?? '',
      body: tpl.body,
    });
    setPreviewText(null);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setPreviewText(null);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.eventKey || !form.body.trim() || !form.name.trim()) {
      toast.error('Event, name and body are required');
      return;
    }
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  function insertVariable(v: string) {
    setForm((f) => ({ ...f, body: `${f.body}{{${v}}}` }));
  }

  async function doPreview() {
    try {
      const res = await api.post('/communications/preview', {
        channel: form.channel,
        eventKey: form.eventKey,
        subject: form.subject,
        body: form.body,
      });
      setPreviewText(res.data);
    } catch {
      toast.error('Preview failed');
    }
  }

  const eventOptions = (events ?? []).map((e) => ({ label: e.label, value: e.eventKey }));
  const channelOptions: { label: string; value: Channel }[] = [
    { label: 'Email', value: 'EMAIL' },
    { label: 'SMS', value: 'SMS' },
    { label: 'WhatsApp', value: 'WHATSAPP' },
  ];

  const tabs = [
    { key: 'templates', label: 'Templates', icon: MessageSquare },
    { key: 'logs', label: 'Message Log', icon: ScrollText },
    { key: 'providers', label: 'Providers', icon: Settings },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Communications"
        subtitle="Template-driven email, SMS & WhatsApp messaging with delivery tracking."
        actions={
          tab === 'templates' && canCreate ? (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New template
            </Button>
          ) : undefined
        }
      />

      <TabBar
        tabs={tabs}
        activeTab={tab}
        onTabChange={(k) => setTab(k as 'templates' | 'logs' | 'providers')}
      />

      {tab === 'templates' && (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <SkeletonTable rows={5} />
            ) : !templates || templates.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title="No custom templates yet"
                subtitle="System default templates are used automatically. Create one to customize a message."
                action={canCreate ? { label: 'New template', onClick: openCreate } : undefined}
              />
            ) : (
              <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('colChannel')}</Th>
                      <Th>{t('colEvent')}</Th>
                      <Th>{t('colName')}</Th>
                      <Th>{t('colLocale')}</Th>
                      <Th>{t('colStatus')}</Th>
                      <Th>{t('colMeta')}</Th>
                      <Th className="text-end">{t('colActions')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {templates.map((tpl) => {
                      const Icon = CHANNEL_ICON[tpl.channel];
                      return (
                        <Tr key={tpl.id}>
                          <Td label={t('colChannel')}>
                            <span className="inline-flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              {tpl.channel}
                            </span>
                          </Td>
                          <Td label={t('colEvent')}>{tpl.eventKey}</Td>
                          <Td label={t('colName')} className="font-medium text-foreground">{tpl.name}</Td>
                          <Td label={t('colLocale')} className="uppercase">{tpl.locale}</Td>
                          <Td label={t('colStatus')}><StatusBadge status={tpl.status} /></Td>
                          <Td label={t('colMeta')}>
                            {tpl.channel === 'WHATSAPP' && tpl.metaStatus !== 'NONE' ? (
                              <StatusBadge status={tpl.metaStatus} />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </Td>
                          <Td label={t('colActions')} className="td-actions">
                            <div className="flex items-center justify-end gap-1">
                              {canSend && (
                                <Button variant="ghost" size="sm" title="Test send"
                                  onClick={() => { setTestTemplate(tpl); setTestTo(''); }}>
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                              {canUpdate && tpl.channel === 'WHATSAPP' && (
                                <Button variant="ghost" size="sm" title="Sync with Meta"
                                  disabled={metaSyncMutation.isPending}
                                  onClick={() => metaSyncMutation.mutate(tpl.id)}>
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              )}
                              {canUpdate && tpl.status !== 'ACTIVE' && (
                                <Button variant="ghost" size="sm" title="Activate"
                                  onClick={() => statusMutation.mutate({ id: tpl.id, action: 'activate' })}>
                                  <CheckCircle2 className="h-4 w-4 text-success-text" />
                                </Button>
                              )}
                              {canUpdate && tpl.status === 'ACTIVE' && (
                                <Button variant="ghost" size="sm" title="Archive"
                                  onClick={() => statusMutation.mutate({ id: tpl.id, action: 'archive' })}>
                                  <Archive className="h-4 w-4" />
                                </Button>
                              )}
                              {canUpdate && (
                                <Button variant="ghost" size="sm" title="Edit" onClick={() => openEdit(tpl)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button variant="ghost" size="sm" title="Delete"
                                  onClick={() => setConfirmDeleteId(tpl.id)}>
                                  <Trash2 className="h-4 w-4 text-destructive-text" />
                                </Button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>

            )}
          </CardContent>
        </Card>
      )}

      {tab === 'logs' && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b p-3">
              <Dropdown
                value={logChannel}
                options={[{ label: 'All channels', value: '' }, ...channelOptions]}
                onChange={(e) => { setLogChannel(e.value); setLogPage(1); }}
                className="w-48"
              />
            </div>
            {logsLoading ? (
              <SkeletonTable rows={6} />
            ) : !logs || logs.data.length === 0 ? (
              <EmptyState icon={ScrollText} title="No messages logged yet"
                subtitle="Sent and generated messages will appear here." />
            ) : (
              <>
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('colWhen')}</Th>
                      <Th>{t('colChannel')}</Th>
                      <Th>{t('colEvent')}</Th>
                      <Th>{t('colTo')}</Th>
                      <Th>{t('colStatus')}</Th>
                      <Th>{t('colProvider')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {logs.data.map((log) => (
                      <Tr key={log.id}>
                        <Td label={t('colWhen')} className="whitespace-nowrap">{formatDate(log.createdAt)}</Td>
                        <Td label={t('colChannel')}>{log.channel}</Td>
                        <Td label={t('colEvent')}>{log.eventKey ?? '—'}</Td>
                        <Td label={t('colTo')}>{log.toAddress}</Td>
                        <Td label={t('colStatus')}>
                          <StatusBadge status={log.status} />
                          {log.error && (
                            <span className="ms-2 text-xs text-destructive-text" title={log.error}>!</span>
                          )}
                        </Td>
                        <Td label={t('colProvider')} className="text-muted-foreground">{log.provider ?? '—'}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
                <PaginationBar
                  page={logs.page}
                  pageSize={logs.limit}
                  total={logs.total}
                  onPageChange={setLogPage}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'providers' && (
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">Provider credentials</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Email (SMTP), SMS (Twilio / webhook) and WhatsApp (Twilio / Meta Business) credentials
                  are managed in Settings → Communications. Templates above use whichever provider is
                  configured there; if none is configured, messages are generated for manual sending.
                </p>
                <Button className="mt-3" variant="outline" onClick={() => navigate('/admin/settings')}>
                  <ExternalLink className="h-4 w-4" /> Open provider settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor */}
      <FormDialog
        open={editorOpen}
        title={editing ? 'Edit template' : 'New template'}
        subtitle={editing ? `Version ${editing.version} · saving creates a new version` : undefined}
        width="46rem"
        submitLabel={editing ? 'Save' : 'Create'}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
        onClose={closeEditor}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Channel</Label>
            <Dropdown
              value={form.channel}
              options={channelOptions}
              disabled={!!editing}
              onChange={(e) => setForm((f) => ({ ...f, channel: e.value }))}
              className="w-full"
            />
          </div>
          <div>
            <Label>Event</Label>
            <Dropdown
              value={form.eventKey}
              options={eventOptions}
              disabled={!!editing}
              filter
              placeholder="Select event"
              onChange={(e) => setForm((f) => ({ ...f, eventKey: e.value }))}
              className="w-full"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="tpl-name">Name</Label>
            <Input id="tpl-name" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label htmlFor="tpl-locale">Locale</Label>
            <Input id="tpl-locale" value={form.locale} disabled={!!editing}
              onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))} />
          </div>
        </div>
        {form.channel === 'EMAIL' && (
          <div>
            <Label htmlFor="tpl-subject">Subject</Label>
            <Input id="tpl-subject" value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          </div>
        )}
        <div>
          <Label htmlFor="tpl-body">Body</Label>
          <Textarea
            id="tpl-body"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            rows={8}
            placeholder="Message body — use {{variable}} placeholders"
          />
        </div>
        {form.channel === 'EMAIL' && selectedEvent?.document && (
          <p className="text-xs text-muted-foreground">{t('poweredByAuto')}</p>
        )}
        {selectedEvent && selectedEvent.variables.length > 0 && (
          <div>
            <Label>Variables (click to insert)</Label>
            <div className="flex flex-wrap gap-1.5">
              {selectedEvent.variables.map((v) => (
                <button key={v} type="button" onClick={() => insertVariable(v)}
                  className="rounded-md bg-muted px-2 py-1 text-xs font-mono text-foreground hover:bg-accent">
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={doPreview}>
            <Eye className="h-4 w-4" /> Preview
          </Button>
          {previewText && (
            <div className="mt-2 rounded-md border bg-muted/40 p-3 text-sm">
              {previewText.subject && (
                <p className="mb-1 font-semibold">{previewText.subject}</p>
              )}
              <pre className="whitespace-pre-wrap font-sans text-foreground">{previewText.body}</pre>
            </div>
          )}
        </div>
      </FormDialog>

      {/* Test send */}
      <Dialog
        visible={!!testTemplate}
        onHide={() => setTestTemplate(null)}
        header="Send test message"
        style={{ width: '28rem' }}
        draggable={false}
      >
        {testTemplate && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send a <strong>{testTemplate.channel}</strong> test for{' '}
              <strong>{testTemplate.eventKey}</strong> using sample data.
            </p>
            <div>
              <Label htmlFor="test-to">
                {testTemplate.channel === 'EMAIL' ? 'Recipient email' : 'Recipient phone'}
              </Label>
              <Input id="test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                placeholder={testTemplate.channel === 'EMAIL' ? 'name@example.com' : '+92...'} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTestTemplate(null)}>Cancel</Button>
              <Button disabled={!testTo.trim() || testSendMutation.isPending}
                onClick={() => testSendMutation.mutate({ template: testTemplate, to: testTo })}>
                <Send className="h-4 w-4" /> Send
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        title="Delete template"
        description="This permanently removes the custom template. The system default will be used instead."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmDeleteId) deleteMutation.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
